import {
  AnalysisService,
  type CreateAnalysisRequestDto,
  type CreateAnalysisResponseDto,
} from '@inference.engine/api-client/web';
import { inferenceHttp } from '@/lib/api/http-client';
import { i18n } from '@/shared/i18n';

// Read operations (getAnalyses/getAnalysisById) and the Analysis/AnalysisListItem
// types now live in `entities/analysis`. This file keeps only the write
// operations, which remain feature-owned.

export const createAnalysis = async (
  body: CreateAnalysisRequestDto,
): Promise<CreateAnalysisResponseDto> => {
  const response = await AnalysisService.createAnalysis({
    client: inferenceHttp,
    body,
  });

  if (!response.data) {
    throw new Error(i18n.t('analysis:analysis.useAnalyses.failedToCreateAnalysis'));
  }

  return response.data;
};

export const retryAnalysis = async (id: string): Promise<CreateAnalysisResponseDto> => {
  const response = await AnalysisService.retryAnalysis({
    client: inferenceHttp,
    path: { id },
  });

  if (!response.data) {
    throw new Error(i18n.t('analysis:analysis.useAnalyses.failedToRetryAnalysis'));
  }

  return response.data;
};

/**
 * Mints (idempotently) the workspace artifact that backs "Save to project" and
 * returns its id. Every artifact field is derived server-side from the analysis.
 */
export const saveAnalysisAsArtifact = async (id: string, workspaceId: string): Promise<string> => {
  const response = await AnalysisService.saveAsArtifact({
    client: inferenceHttp,
    path: { id },
    query: { workspaceId },
  });

  if (!response.data) {
    throw new Error(i18n.t('analysis:analysis.useAnalyses.failedToSaveAnalysis'));
  }

  return response.data.id;
};

export const deleteAnalysis = async (id: string): Promise<undefined> => {
  await AnalysisService.deleteAnalysis({
    client: inferenceHttp,
    path: { id },
  });
};

/**
 * Which of a report's two audience blocks a call is about. A report carries one or the other:
 * audiences read out of the documents it was created with, or the ones IQ suggested for a
 * report that was given none.
 */
export type AudienceDraftEnvelope = 'suggestedAudiences' | 'uploadedAudiences';

/**
 * Asks a report to build personas for the audiences it holds. Safe to call again: the server
 * starts one run at a time, and this also serves the retry, so a caller only has to know that
 * it wants them built.
 */
export const generateAudienceDrafts = async (
  id: string,
  envelope: AudienceDraftEnvelope,
): Promise<void> => {
  // Without `throwOnError` the client resolves on a 403 or a 500, so a refused request would
  // look like a run that started and the section would wait for audiences nobody is building.
  const options = { client: inferenceHttp, path: { id }, throwOnError: true } as const;

  if (envelope === 'uploadedAudiences') {
    await AnalysisService.generateUploadedAudiences(options);
    return;
  }

  await AnalysisService.generateSuggestedAudiences(options);
};

/** Puts one audience in the Knowledge Hub, answering with the persona it became. */
export const saveAudienceDraft = async (
  id: string,
  envelope: AudienceDraftEnvelope,
  draftId: string,
): Promise<{ personaId: string }> => {
  const options = { client: inferenceHttp, path: { id, draftId }, throwOnError: true } as const;

  const response =
    envelope === 'uploadedAudiences'
      ? await AnalysisService.saveUploadedAudience(options)
      : await AnalysisService.saveSuggestedAudience(options);

  if (!response.data) {
    throw new Error(i18n.t('analysis:analysisResult.suggestedAudiences.couldNotSaveThe'));
  }

  return response.data as { personaId: string };
};
